import type { FC } from 'react';

import { useDeviceType, useUserAgentDevice } from '@packages/shared';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { FilterSettingsFragmentFragmentDoc } from '@packages/gql/generated/shopping/FilterSettingsFragmentFragmentDoc';

import { useSearchFilters } from '../../hooks/useFilters';
import { ErasedFiltersNotification } from '../ErasedFiltersNotification';
import type { FilterSettingsMobileProps } from '../FilterSettingsMobile';
import { FilterSettingsMobile } from '../FilterSettingsMobile';
import { FilterSettingsDesktop } from '../FilterSettingsDesktop';
import { SelectedFilterValueList } from '../SelectedFilterValueList';

/* GraphQL */ `
  fragment FilterSettingsFragment on SearchProductResult {
    ...UseSearchFiltersFragment
    ...UseSortingFragment
    ...SelectedFilterValueListFragment
    ...FilterSettingsDesktopFragment
    ...FilterSettingsMobileFragment
  }
`;

export type FilterSettingsProps = {
  maskedData: FragmentType<typeof FilterSettingsFragmentFragmentDoc>;
  showRemovedFiltersNotification?: boolean;
  renderCategoryDialogContent: FilterSettingsMobileProps['renderCategoryDialogContent'];
};

/**
 * Responsive component to handle all filter interactions
 * */
export const FilterSettings: FC<FilterSettingsProps> = ({
  maskedData,
  showRemovedFiltersNotification,
  renderCategoryDialogContent,
}) => {
  const userAgentDevice = useUserAgentDevice();
  const { isMobile } = useDeviceType();

  const data = getFragmentData(FilterSettingsFragmentFragmentDoc, maskedData);

  const { selectedFilterValues, pushFilterChange } = useSearchFilters(data);

  return (
    <>
      <ErasedFiltersNotification
        show={showRemovedFiltersNotification}
        onShow={() => pushFilterChange([])}
      />
      {isMobile || userAgentDevice === 'mobile' ? (
        <FilterSettingsMobile
          maskedData={data}
          selectedFilterValues={selectedFilterValues}
          applyCloseButtonPaddingWorkaround
          onSubmit={pushFilterChange}
          renderCategoryDialogContent={renderCategoryDialogContent}
        />
      ) : (
        <FilterSettingsDesktop
          maskedData={data}
          selectedFilterValues={selectedFilterValues}
          onSubmit={pushFilterChange}
        />
      )}
      <SelectedFilterValueList maskedData={data} showColors />
    </>
  );
};
